import React, { Fragment } from 'react'
import { withRouter } from 'react-router'

import { useUnit } from 'effector-react'

import { useMemo, useSelector, useCallback, useMediaQueries } from 'hooks'

import { urlTo } from 'helpers/router'

import { $dictsStore } from 'app/effector/dicts'

import { isClientApproved } from 'app/redux/selectors'

import { Paper } from 'components/atoms/Paper'

import { useStrapiBonuses } from 'app/pages/Dashboard/PromoBanner/hooks/useStrapiBonuses'
import { ProfileInfo } from 'app/pages/Dashboard/UserProfile/Profile/ProfileInfo'
import { useReferralCode } from 'app/pages/Dashboard/UserProfile/ReferralCode/hooks/useReferralCode'

import { MenuItem } from '../Menu/MenuItem'

import './ProfileMenu.css'

const ProfileMenu = withRouter(({ location }): JSX.Element => {
  const { bonuses } = useStrapiBonuses()
  const { isPromoEnabled } = useUnit($dictsStore)
  const { isReferralCodeValid } = useReferralCode()
  const isReferralEnabled = useSelector(isClientApproved) && isPromoEnabled && isReferralCodeValid
  const { desktop } = useMediaQueries()

  const urlWithBack = useCallback(
    (url) => {
      // prevents infite adding of search params
      if (urlTo(url) === location.pathname) return location.pathname + location.search

      return urlTo(url, null, { back: location.pathname + location.search })
    },
    [location.pathname, location.search],
  )

  const actions = useMemo(() => {
    return [
      {
        href: urlWithBack('dashboard.user-profile.personal-info'),
        text: 'Personal info',
        icon: 'person-24',
        isVisible: true,
        dataTestId: 'personalInfoLink',
      },
      {
        href: urlWithBack('dashboard.user-profile'),
        text: 'Settings',
        icon: 'settings-24',
        isVisible: true,
        dataTestId: 'settingsLink',
      },
      { href: 'https://help.investengine.com/hc/en-gb', text: 'Help', icon: 'help-24', blank: true, isVisible: true },
      {
        href: urlWithBack('dashboard.user-profile.referral'),
        text: bonuses?.referIsaOrGeneralBonusMax ? `Earn up to ${bonuses.referIsaOrGeneralBonusMax}` : 'Earn',
        icon: 'gift-24',
        isVisible: isReferralEnabled,
        dataTestId: 'referralPageLink',
      },
      {
        href: urlTo('logout'),
        text: 'Log out',
        icon: 'logout-24',
        noArrow: true,
        textColor: 'error',
        iconColor: 'var(--status-error)',
        isVisible: true,
        dataTestId: 'logoutButton',
      },
    ]
  }, [bonuses?.referIsaOrGeneralBonusMax, isReferralEnabled, urlWithBack])

  return (
    <Fragment>
      <ProfileInfo />

      <Paper top={32} right={desktop ? 24 : 0} left={desktop ? 24 : 0}>
        <ul className="ProfileMenu-List ">
          {actions.map(({ text, icon, href, blank, noArrow, textColor, iconColor, isVisible, dataTestId }) => {
            if (!isVisible) return null

            return (
              <li className="ProfileMenu-Item" key={text}>
                <MenuItem
                  icon={icon}
                  title={text}
                  link={href ?? ''}
                  blank={blank}
                  noArrow={noArrow}
                  hard={false}
                  titleColor={textColor}
                  iconColor={iconColor}
                  dataTestId={dataTestId}
                />
              </li>
            )
          })}
        </ul>
      </Paper>
    </Fragment>
  )
})

export { ProfileMenu }
